import { cn } from '@/lib/cn';

/**
 * A read-only icon+value+label tile — the display counterpart of StatField
 * (which wraps an editable input in the same chrome). Originally local to
 * StageSummaryView.jsx's Vitals read view; promoted here once PreConsultStage
 * needed the same "vitals recorded earlier this visit" strip cascaded in
 * from the Vitals stage, so both places render one vital sign identically.
 */
export interface StatTileProps {
  icon: string;
  label: string;
  value: string | number | null | undefined;
  unit?: string;
  /** Amber tile treatment for a reading outside the expected range — advisory only. */
  warn?: boolean;
  /** A short statement of the cut-off that caused `warn`, e.g. "↑ >140/90",
   * printed under the label. Rendered only alongside `warn` — a cut-off shown
   * on a normal reading is noise. See VitalTile, which supplies both from
   * lib/vitalThresholds.ts. */
  hint?: string;
}

function isEmptyValue(v: unknown) {
  return v === null || v === undefined || v === '';
}

export function StatTile({ icon, label, value, unit, warn, hint }: StatTileProps) {
  if (isEmptyValue(value)) return null;
  return (
    // `bg-card` rather than a translucent tint so a tile reads as a raised
    // chip on any surface — these now sit on the muted read-only vitals strip
    // as well as on plain white cards.
    <div className={cn('flex min-w-[86px] flex-col items-center gap-0.5 rounded-[var(--radius)] border px-3 py-2 text-center', warn ? 'border-warn/40 bg-warn-soft' : 'border-line bg-card')}>
      <div className="text-base leading-none">{icon}</div>
      <div className={cn('text-[15px] font-bold leading-tight', warn ? 'text-warn-ink' : 'text-ink')}>
        {value}
        {unit ? <span className="text-[10.5px] font-medium"> {unit}</span> : null}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</div>
      {warn && hint && <div className="text-[9.5px] font-medium leading-tight text-warn-ink">{hint}</div>}
    </div>
  );
}
